/**
 * Context Parser - Extracts tangle blocks with their surrounding context
 *
 * Builds on the tangle parser to capture the heading and description
 * that precede each code block, enabling interactive script generation.
 */

import { extractTangleBlocks } from '../tangle/index.js';
import type { ContextualBlock, BlockContext } from './types.js';

/**
 * Regex patterns for parsing
 */
const HEADING_REGEX = /^(#{1,6})\s+(?:(\d+(?:\.\d+)*)\.)?\s*(.+)$/;
const TANGLE_CUSTOM_REGEX = /^<!--\s*tangle:\s*([^>]+?)\s*-->/;
const TANGLE_QUARTO_REGEX = /^```.*tangle="([^"]+)"/;
const FENCE_START_REGEX = /^```(\w*)/;
const FENCE_END_REGEX = /^```\s*$/;

/**
 * Internal structure to track tangle marker positions
 */
interface TangleMarkerInfo {
  lineNumber: number;
  target: string;
  format: 'custom' | 'quarto';
  language: string | null;
}

function splitTangleTargets(value: string): string[] {
  return value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

/**
 * Internal structure to track heading positions
 */
interface HeadingInfo {
  lineNumber: number;
  text: string;
  level: number;
  number: string | null;
}

/**
 * Extract all tangle blocks with their surrounding context
 *
 * For each tangle block, captures:
 * - The most recent heading above the block
 * - The text between the heading and the tangle marker (description)
 * - The detected language from the code fence
 *
 * @param wovenMarkdown - The woven markdown content (embeds expanded, variables may or may not be substituted)
 * @returns Array of ContextualBlock objects with context information
 */
export function extractContextualBlocks(wovenMarkdown: string): ContextualBlock[] {
  // Step 1: Get basic tangle blocks using existing parser
  const blocks = extractTangleBlocks(wovenMarkdown);

  if (blocks.length === 0) {
    return [];
  }

  // Step 2: Scan markdown to find heading positions and tangle marker positions
  const lines = wovenMarkdown.split('\n');
  const headings: HeadingInfo[] = [];
  const tangleMarkers: TangleMarkerInfo[] = [];

  let inCodeBlock = false;
  let currentLanguage: string | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNumber = i + 1; // 1-based

    // Track code block state to avoid matching headings inside code blocks
    if (!inCodeBlock && FENCE_START_REGEX.test(line)) {
      const match = line.match(FENCE_START_REGEX);
      currentLanguage = match && match[1] ? match[1].toLowerCase() : null;
      inCodeBlock = true;

      // Check if this is a Quarto-style tangle
      const quartoMatch = line.match(TANGLE_QUARTO_REGEX);
      if (quartoMatch) {
        tangleMarkers.push({
          lineNumber,
          target: quartoMatch[1],
          format: 'quarto',
          language: extractQuartoLanguage(line),
        });
      }
      continue;
    }

    if (inCodeBlock && FENCE_END_REGEX.test(line)) {
      inCodeBlock = false;
      currentLanguage = null;
      continue;
    }

    if (inCodeBlock) {
      continue;
    }

    // Check for heading
    const headingMatch = line.match(HEADING_REGEX);
    if (headingMatch) {
      headings.push({
        lineNumber,
        level: headingMatch[1].length,
        number: headingMatch[2] || null,
        text: headingMatch[3].trim(),
      });
      continue;
    }

    // Check for custom tangle marker
    const customMatch = line.match(TANGLE_CUSTOM_REGEX);
    if (customMatch) {
      // Look ahead to find the language from the next code fence
      const nextFenceLanguage = findNextFenceLanguage(lines, i + 1);
      const targets = splitTangleTargets(customMatch[1]);
      for (const target of targets) {
        tangleMarkers.push({
          lineNumber,
          target,
          format: 'custom',
          language: nextFenceLanguage,
        });
      }
    }
  }

  // Step 3: Associate each tangle block with its context
  // Match blocks to markers by target and order
  const markersByTarget = new Map<string, TangleMarkerInfo[]>();
  for (const marker of tangleMarkers) {
    const existing = markersByTarget.get(marker.target) || [];
    existing.push(marker);
    markersByTarget.set(marker.target, existing);
  }

  // Track which marker index we're on for each target
  const markerIndices = new Map<string, number>();

  const contextualBlocks: ContextualBlock[] = blocks.map((block) => {
    // Get the next marker for this target
    const markers = markersByTarget.get(block.target) || [];
    const markerIndex = markerIndices.get(block.target) || 0;
    const marker = markers[markerIndex];
    markerIndices.set(block.target, markerIndex + 1);

    const context = buildBlockContext(
      marker?.lineNumber || 0,
      headings,
      lines
    );

    return {
      target: block.target,
      content: block.content,
      blockIndex: block.blockIndex,
      format: block.format,
      context,
      language: marker?.language || null,
    };
  });

  return contextualBlocks;
}

/**
 * Find the language specifier from the next code fence
 */
function findNextFenceLanguage(lines: string[], startIndex: number): string | null {
  for (let i = startIndex; i < lines.length && i < startIndex + 5; i++) {
    const match = lines[i].match(FENCE_START_REGEX);
    if (match) {
      return match[1] ? match[1].toLowerCase() : null;
    }
  }
  return null;
}

/**
 * Extract language from Quarto-style code fence
 * e.g., ```{bash, tangle="script.sh"} -> "bash"
 */
function extractQuartoLanguage(line: string): string | null {
  const match = line.match(/^```\{(\w+)/);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Build context information for a block at a given line
 */
function buildBlockContext(
  tangleLineNumber: number,
  headings: HeadingInfo[],
  lines: string[]
): BlockContext {
  // Find the most recent heading before this tangle marker
  let closestHeading: HeadingInfo | null = null;
  for (const heading of headings) {
    if (heading.lineNumber < tangleLineNumber) {
      closestHeading = heading;
    } else {
      break;
    }
  }

  // Extract description: text between heading and tangle marker
  let description = '';
  if (closestHeading && tangleLineNumber > 0) {
    const descLines: string[] = [];
    const startLine = closestHeading.lineNumber; // Line after heading (0-based index = lineNumber)
    const endLine = tangleLineNumber - 1; // Line before tangle marker (0-based index = lineNumber - 2)

    for (let i = startLine; i < endLine && i < lines.length; i++) {
      const line = lines[i];
      // Skip empty lines at the start
      if (descLines.length === 0 && line.trim() === '') {
        continue;
      }
      // Stop at code fences or other tangle markers
      if (FENCE_START_REGEX.test(line) || TANGLE_CUSTOM_REGEX.test(line)) {
        break;
      }
      descLines.push(line);
    }

    // Trim trailing empty lines
    while (descLines.length > 0 && descLines[descLines.length - 1].trim() === '') {
      descLines.pop();
    }

    description = descLines.join('\n');
  }

  return {
    heading: closestHeading?.text || null,
    headingLevel: closestHeading?.level || null,
    headingNumber: closestHeading?.number || null,
    description,
    lineNumber: tangleLineNumber,
  };
}

/**
 * Group contextual blocks by their target script
 *
 * @param blocks - Array of ContextualBlock objects
 * @returns Map of target filename to array of blocks (sorted by blockIndex)
 */
export function groupContextualBlocksByTarget(
  blocks: ContextualBlock[]
): Map<string, ContextualBlock[]> {
  const grouped = new Map<string, ContextualBlock[]>();

  for (const block of blocks) {
    const existing = grouped.get(block.target) || [];
    existing.push(block);
    grouped.set(block.target, existing);
  }

  // Sort each group by blockIndex to preserve document order
  for (const [, blockList] of grouped) {
    blockList.sort((a, b) => a.blockIndex - b.blockIndex);
  }

  return grouped;
}
